import { create } from "zustand";
import { formatDate } from "@/lib/utils/date-formatter";
import { agentApi } from "@/services/agentApi";

export const createAgentTemplatesSlice = (set, get) => ({
  isGeneratingInstructions: false,
  generatedInstructions: null,
  generationError: null,
  selectedAgentId: null,

  setSelectedAgentId: (id) => set({ selectedAgentId: id }),
  clearSelectedAgentId: () => set({ selectedAgentId: null }),

  generateSystemInstructions: async (payload, options = {}) => {
    try {
      set({ isGeneratingInstructions: true, generationError: null });

      const isWizardPayload = options.isWizard ?? Array.isArray(payload);
      const response = isWizardPayload
        ? await agentApi.generateSystemInstructionsFromWizard(payload)
        : await agentApi.generateSystemInstructions(payload);

      const fallbackValue = !isWizardPayload
        ? typeof payload === "string"
          ? payload
          : payload?.content ?? null
        : null;

      const instructions =
        response?.result ??
        response?.instructions ??
        (typeof response === "string" ? response : null) ??
        fallbackValue;

      set({
        generatedInstructions: instructions,
        isGeneratingInstructions: false,
      });

      return instructions;
    } catch (error) {
      console.error("Error generating instructions:", error);
      set({
        generationError: error.message,
        isGeneratingInstructions: false,
      });
      return null;
    }
  },

  clearGeneratedInstructions: () =>
    set({
      generatedInstructions: null,
      generationError: null,
    }),
});

export const createAgentUiSlice = (set, get) => ({
  isCreating: false,
  fileModalOpen: false,
  modalType: null,
  deleteModalOpen: false,
  agentToDelete: null,
  isTemplatePopoverOpen: false,
  formDefaultValues: null,
  showDeleteModal: false,

  // Modal actions
  setIsCreating: (isCreating) => set({ isCreating }),
  openFileModal: (type) => set({ fileModalOpen: true, modalType: type }),
  closeFileModal: () => set({ fileModalOpen: false, modalType: null }),

  openDeleteModal: (agentId) =>
    set({ deleteModalOpen: true, agentToDelete: agentId }),
  closeDeleteModal: () => set({ deleteModalOpen: false, agentToDelete: null }),

  // Template popover actions
  setTemplatePopoverOpen: (isOpen) => set({ isTemplatePopoverOpen: isOpen }),

  handleTemplateSelect: (template) => {
    const { control } = get();
    set((state) => ({
      ...state,
      formDefaultValues: {
        ...state.formDefaultValues,
        systemInstructions: template.content,
      },
    }));
    // Force update the form field
    control?.setValue("systemInstructions", template.content);
    set({ isTemplatePopoverOpen: false });
  },
  // Form actions
  setFormDefaultValues: (values) => set({ formDefaultValues: values }),
  resetFormState: () =>
    set({
      isCreating: false,
      formDefaultValues: null,
      isTemplatePopoverOpen: false,
    }),

  handleEditAgent: (agent) => {
    const { setSelectedAgentId, setFormDefaultValues, setIsCreating } = get();
    setSelectedAgentId(agent.id);
    setIsCreating(false);
    setFormDefaultValues({
      name: agent.name,
      systemInstructions: agent.systemInstructions,
      model: agent.model,
      tools: agent.tools,
    });
  },

  handleCreateNew: () => {
    const { setSelectedAgentId, setIsCreating, setFormDefaultValues } = get();
    setSelectedAgentId(null);
    setIsCreating(true);
    setFormDefaultValues({
      id: 0,
      name: "",
      systemInstructions: "",
      fileId: "",
      isFileSearchEnabled: false,
      openAiAssistantId: "",
      projectId: 0,
      openAiProjectId: "",
      authorizerId: "",
      authorizer: "",
    });
  },

  showDeleteConfirmation: (agentId) =>
    set({ showDeleteModal: true, agentToDelete: agentId }),
  hideDeleteConfirmation: () =>
    set({ showDeleteModal: false, agentToDelete: null }),
});
